import React, { useState } from 'react';
import { Settings, Save, CheckCircle2, Sliders, Database, Layers } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export const MappingRulesConfig: React.FC = () => {
  const { logAction } = useAuth();
  const [tolerance, setTolerance] = useState('0.00');
  const [autoGroupThreshold, setAutoGroupThreshold] = useState('100');
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setSavedSuccess(true);
    logAction(
      'CONFIG_UPDATE',
      `Admin updated reconciliation tolerance to ₹${tolerance} and auto-group threshold to ${autoGroupThreshold}.`
    );
    setTimeout(() => setSavedSuccess(false), 3000);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-bold text-slate-900 dark:text-white">
              Global Reconciliation Engine & Mapping Rules
            </h2>
            <span className="text-[11px] px-2 py-0.5 rounded bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-300 font-semibold border border-purple-200 dark:border-purple-800">
              Rule Parameters
            </span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Configure default matching tolerances, multi-bill aggregation parameters, and ERP gateway synchronization rules.
          </p>
        </div>

        {savedSuccess && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 text-xs font-semibold border border-emerald-200 dark:border-emerald-800">
            <CheckCircle2 className="w-4 h-4" />
            Saved Successfully!
          </span>
        )}
      </div>

      <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Rule 1: Multi-bill Aggregation Settings */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
          <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100 dark:border-slate-800">
            <Layers className="w-5 h-5 text-brand-600" />
            <h3 className="font-bold text-sm text-slate-900 dark:text-white">
              Multi-Bill Aggregation Parameters
            </h3>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
              Grouping Key Strategy
            </label>
            <select
              defaultValue="payment_id_exact"
              className="w-full px-3 py-2 rounded-lg text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
            >
              <option value="payment_id_exact">Primary Matching Key: Payment ID (Strict)</option>
              <option value="payment_id_normalized">Normalized (Ignore Hyphens & Spaces)</option>
            </select>
            <p className="text-[11px] text-slate-500 mt-1">
              All ERP rows sharing the identical Payment ID are summed before comparing with Razorpay.
            </p>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
              Rounding Variance Tolerance (INR ₹)
            </label>
            <input
              type="text"
              value={tolerance}
              onChange={(e) => setTolerance(e.target.value)}
              placeholder="0.00"
              className="w-full px-3 py-2 rounded-lg text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white font-mono"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Variance threshold below which differences are marked as Exact Match (default: ₹0.00).
            </p>
          </div>

          <div className="pt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={caseSensitive}
                onChange={(e) => setCaseSensitive(e.target.checked)}
                className="rounded text-brand-600 focus:ring-brand-500"
              />
              <span className="text-xs text-slate-700 dark:text-slate-300 font-medium">
                Case-sensitive Payment ID matching
              </span>
            </label>
          </div>
        </div>

        {/* Rule 2: Exception Thresholds & Alerts */}
        <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
          <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100 dark:border-slate-800">
            <Sliders className="w-5 h-5 text-amber-500" />
            <h3 className="font-bold text-sm text-slate-900 dark:text-white">
              Audit Alerts & AI Diagnostic Rules
            </h3>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
              High Variance Alert Cutoff (INR ₹)
            </label>
            <input
              type="number"
              defaultValue={50000}
              className="w-full px-3 py-2 rounded-lg text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white font-mono"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Batches with cumulative exception amount exceeding this threshold trigger 'Critical Review'.
            </p>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1">
              AI Forensic Reasoning Depth
            </label>
            <select
              defaultValue="comprehensive"
              className="w-full px-3 py-2 rounded-lg text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
            >
              <option value="comprehensive">Comprehensive (Root Cause + Technical + Remediation)</option>
              <option value="summary_only">Summary Only</option>
            </select>
          </div>

          <div className="pt-5">
            <button
              type="submit"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-semibold text-xs shadow-md transition"
            >
              <Save className="w-4 h-4" />
              <span>Save Configuration Rules</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};
